import React, { type Ref, useCallback, useState } from 'react';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { panelEntryPoint } from '@atlassian/entry-point-fixtures/panelEntryPoint';
import {
	PanelActionClose,
	PanelActionGroup,
	PanelBody,
	PanelContainer,
	PanelFooter,
	PanelHeader,
	PanelProvider,
	PanelsRenderer,
	PanelTitle,
	Trigger,
	useCloseCurrentPanel,
} from '@atlassian/panel-system';
import { PanelContent } from '@atlassian/panel-system/panel-content';

import { RelayEnvironmentWrapper } from '../utils/relay-environment-wrapper';

const styles = cssMap({
	layout: {
		height: '100vh',
		width: '100%',
	},
	mainContent: {
		flex: 1,
		paddingBlockStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	buttonGroup: {
		display: 'flex',
		gap: token('space.100'),
		flexWrap: 'wrap',
	},
	panelWrapper: {
		borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		width: '480px',
	},
});

/**
 * Panel component that demonstrates unsaved changes handling with onBeforeClose.
 * This shows how to prevent panel closing when there are unsaved changes, and how
 * to show a confirmation dialog using @atlaskit/modal-dialog.
 */
function UnsavedChangesPanel() {
	const closePanel = useCloseCurrentPanel();
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [formValue, setFormValue] = useState('');
	const [showDiscardModal, setShowDiscardModal] = useState(false);

	const handleBeforeClose = async () => {
		if (hasUnsavedChanges) {
			// Show the discard confirmation modal instead of using window.confirm
			setShowDiscardModal(true);
			return false; // Prevent panel from closing
		}
		return true;
	};

	const handleDiscard = useCallback(() => {
		setShowDiscardModal(false);
		setHasUnsavedChanges(false);
		setFormValue('');
		closePanel();
	}, [closePanel]);

	const handleCancelDiscard = useCallback(() => {
		setShowDiscardModal(false);
	}, []);

	const handleSave = () => {
		setHasUnsavedChanges(false);
	};

	const handleCancel = () => {
		setFormValue('');
		setHasUnsavedChanges(false);
	};

	return (
		<>
			<Box xcss={styles.panelWrapper}>
				<PanelContainer testId="unsaved-changes-panel">
					<PanelHeader>
						<PanelTitle>Edit Form</PanelTitle>
						<PanelActionGroup>
							<PanelActionClose
								onBeforeClose={handleBeforeClose}
								onClick={closePanel}
								testId="close-button"
							/>
						</PanelActionGroup>
					</PanelHeader>
					<PanelContent>
						<PanelBody>
							<Stack space="space.200">
								<Text>
									This example demonstrates handling unsaved changes when closing a panel using{' '}
									<code>onBeforeClose</code> with <code>@atlaskit/modal-dialog</code>.
								</Text>
								<Text color="color.text.subtle" size="small">
									Try editing the field below, then attempt to close the panel. You'll see a modal
									dialog asking if you want to discard your changes.
								</Text>
								<div>
									<label htmlFor="form-field">
										<Text as="span" size="small" weight="medium">
											Field Value:
										</Text>
									</label>
									<input
										id="form-field"
										type="text"
										value={formValue}
										onChange={(e) => {
											setFormValue(e.target.value);
											setHasUnsavedChanges(true);
										}}
									/>
								</div>
								{hasUnsavedChanges && (
									<Text color="color.text.warning" size="small">
										⚠️ You have unsaved changes. Try closing the panel to see the confirmation
										modal.
									</Text>
								)}
							</Stack>
						</PanelBody>
					</PanelContent>
					<PanelFooter>
						<ButtonGroup label="Panel actions">
							<Button appearance="subtle" onClick={handleCancel}>
								Cancel
							</Button>
							<Button appearance="primary" onClick={handleSave} isDisabled={!hasUnsavedChanges}>
								Save
							</Button>
						</ButtonGroup>
					</PanelFooter>
				</PanelContainer>
			</Box>

			<ModalTransition>
				{showDiscardModal && (
					<Modal onClose={handleCancelDiscard} testId="discard-modal" width="small">
						<ModalHeader hasCloseButton>
							<ModalTitle appearance="danger" isMultiline={false}>
								Discard your changes?
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Box as="p">If you leave, your changes will be lost.</Box>
						</ModalBody>
						<ModalFooter>
							<Button appearance="subtle" onClick={handleCancelDiscard}>
								Cancel
							</Button>
							<Button appearance="danger" onClick={handleDiscard}>
								Discard changes
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		</>
	);
}

function UnsavedChangesExample() {
	return (
		<Flex xcss={styles.layout}>
			<Box xcss={styles.mainContent}>
				<Stack space="space.300">
					<Heading size="medium">Unsaved Changes Example</Heading>
					<Text>
						This example demonstrates handling unsaved changes when closing a panel using the{' '}
						<code>onBeforeClose</code> callback.
					</Text>
					<Stack space="space.200">
						<Heading size="small" as="h3">
							How it works
						</Heading>
						<Text color="color.text.subtle" size="small">
							The <code>onBeforeClose</code> callback is called before the panel closes. If it
							returns <code>false</code> (or a Promise resolving to <code>false</code>), the close
							action is prevented. This example uses <code>@atlaskit/modal-dialog</code> to show a
							discard confirmation modal instead of using <code>window.confirm</code>.
						</Text>
						<Box xcss={styles.buttonGroup}>
							<Trigger panel={panelEntryPoint} params={{}}>
								{({ ref }) => (
									<Button ref={ref as Ref<HTMLButtonElement>} testId="open-panel">
										Open Edit Form
									</Button>
								)}
							</Trigger>
						</Box>
					</Stack>
				</Stack>
			</Box>

			<PanelsRenderer>
				<UnsavedChangesPanel />
			</PanelsRenderer>
		</Flex>
	);
}

export default function UnsavedChangesExampleWithProvider(): React.JSX.Element {
	return (
		<RelayEnvironmentWrapper>
			<PanelProvider>
				<UnsavedChangesExample />
			</PanelProvider>
		</RelayEnvironmentWrapper>
	);
}
